import { BrButton, BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import ErrorMessage from 'components/errorMessage/errorMessage';
import { useNotificacao } from 'hooks/useNotificacao';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { IReceberGuiaRecolhimentoConsultaPagamento } from 'interfaces/IGuiaRecolhimento';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-dsgov';
import styles from './statusPagamento.module.scss';
import StatusPagamentoCard from './statusPagamentoCard/statusPagamentoCard';

interface StatusPagamentoProps {
  stepStatus: number;
  setIsModalOpcoesPagamentoOpened: React.Dispatch<React.SetStateAction<boolean>>;
  copiarGuiaRecolhimento: (params: any) => void;
}

function StatusPagamento({
  stepStatus,
  setIsModalOpcoesPagamentoOpened,
  copiarGuiaRecolhimento,
}: StatusPagamentoProps) {
  const [stepStatusPagamento, setStepStatusPagamento] = useState(stepStatus);
  const [isModalConfirmacaoOpened, setIsModalConfirmacaoOpened] = useState(false);
  const [isReportOverlayOpen, setIsReportOverlayOpen] = useState<boolean>(false);
  const [opcaoPagamento, setOpcaoPagamento] = useState<string>('');
  const { getConsultarPagamentoGuia, getImprimirBoleto } = useBackEndRequests();

  const urlParams = new URLSearchParams(window.location.search);
  const guiaRecolhimentoId = urlParams.get('id') || '';

  const mostrarNotificacao = useNotificacao();

  const baixarBoleto = async () => {
    if (isReportOverlayOpen === false && dadosPagamentoData) {
      processarReport(getImprimirBoleto, 'boleto');
    }
  };

  const processarReport = async (fnGetReport: (guiaRecolhimentoId: string) => Promise<Blob>, tipoArquivo: string) => {
    setIsReportOverlayOpen(true);

    await fnGetReport(guiaRecolhimentoId)
      .then((retorno) => {
        prepararReportDownload(retorno, tipoArquivo);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Não foi possível baixar o arquivo solicitado neste momento',
          ...(err && { complementoSentryId: err }),
        });
      })
      .finally(() => {
        setIsReportOverlayOpen(false);
      });
  };

  const prepararReportDownload = (retorno: Blob, tipoArquivo: string) => {
    const boletoImpresso = new Blob([retorno], { type: 'application/pdf' });

    const url = URL.createObjectURL(boletoImpresso);

    const dataSituacaoBoleto = dadosPagamentoData?.dataSituacao;

    const nomeArquivo = `${dataSituacaoBoleto}`;

    const link = document.createElement('a');
    link.href = url;
    link.download = `${tipoArquivo}_${nomeArquivo}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const {
    data: dadosPagamentoData,
    isLoading,
    refetch,
    error,
  } = useQuery<IReceberGuiaRecolhimentoConsultaPagamento, IErrorWithSentryId>({
    queryKey: ['getConsultarPagamentoGuia', guiaRecolhimentoId],
    queryFn: () => getConsultarPagamentoGuia(guiaRecolhimentoId),
    refetchOnWindowFocus: true,
    enabled: stepStatusPagamento === 1,
    staleTime: 0,
  });

  useEffect(() => {
    if (dadosPagamentoData && dadosPagamentoData.situacaoPagamento) {
      setOpcaoPagamento(dadosPagamentoData.formaPagamento);
      switch (dadosPagamentoData.situacaoPagamento) {
        case 'Em preenchimento':
        case 'Criado':
          setStepStatusPagamento(1);
          break;
        case 'Pago':
          setStepStatusPagamento(3);
          break;
        case 'Vencido':
        case 'Cancelado':
        case 'Rejeitado':
          setStepStatusPagamento(4);
          break;
        default:
          setStepStatusPagamento(1);
          break;
      }
    }
  }, [dadosPagamentoData]);

  const getStatusByStepAndCardNumber = (cardNumber: number) => {
    switch (cardNumber) {
      case 1:
        return stepStatusPagamento >= 1 ? 'success' : 'standBy';
      case 2:
        return stepStatusPagamento === 1
          ? 'running'
          : stepStatusPagamento === 2
            ? 'standBy'
            : stepStatusPagamento === 3
              ? 'success'
              : 'error';
      case 3:
        return stepStatusPagamento === 1
          ? 'standBy'
          : stepStatusPagamento === 2
            ? 'running'
            : stepStatusPagamento === 3
              ? 'success'
              : 'error';
      default:
        return 'standBy';
    }
  };

  const getLabelByOpcaoPagamento = (opcaoPagamento: string) => {
    switch (opcaoPagamento) {
      case 'CARTAO_CREDITO':
      case 'PIX':
        return 'Reemitir Guia de Recolhimento';
      case 'BOLETO':
        return 'Baixar boleto';
      default:
        return 'Reimprimir guia de recolhimento';
    }
  };

  const confirmarReemitirGuia = () => {
    setIsModalConfirmacaoOpened(false);
    copiarGuiaRecolhimento(true);
  };

  const getButtonByStatusPagamento = () => {
    switch (stepStatusPagamento) {
      case 1:
        return (
          <BrButton
            key={'action-button-' + opcaoPagamento}
            primary
            className={styles.botao}
            icon='file-download'
            onClick={() => {
              opcaoPagamento === 'BOLETO' ? baixarBoleto() : setIsModalConfirmacaoOpened(true);
            }}
          >
            {getLabelByOpcaoPagamento(opcaoPagamento)}
          </BrButton>
        );
      case 3:
        return (
          <BrButton primary className={styles.botao} icon='file-download' disabled onClick={() => () => {}}>
            Guia de recolhimento paga com sucesso!
          </BrButton>
        );
      case 4:
        return (
          <BrButton primary className={styles.botao} icon='file-download' disabled onClick={() => () => {}}>
            Guia vencida, cancelada ou pagamento recusado. Gerar nova guia de recolhimento.
          </BrButton>
        );
    }
  };

  const conteudo = () => {
    if (isReportOverlayOpen)
      return (
        <div className={styles.loadingOverlay}>
          <BrLoading large label='Processando Operação...' />
        </div>
      );
    if (isLoading) {
      return (
        <div data-testid='loading-container'>
          <BrLoading large />
        </div>
      );
    }
    if (error) {
      return (
        <ErrorMessage
          errorMessage={'Erro ao consultar status do pagamento.'}
          errorTitle='Erro ao consultar pagamento.'
        />
      );
    }
    if (!dadosPagamentoData) {
      return <div>Nenhum dado de pagamento encontrado.</div>;
    }

    if (dadosPagamentoData) {
      return (
        <div>
          <Modal title='Deseja reemitir a guia?' useScrim modalOpened={isModalConfirmacaoOpened}>
            <Modal.Body>
              Já foi gerada uma forma de pagamento para esta guia de recolhimento, ao reemitir, uma nova guia de
              recolhimento será gerada com cópia dos dados desta guia de recolhimento.
            </Modal.Body>
            <Modal.Footer justify-content='end'>
              <Button secondary small m={2} onClick={() => setIsModalConfirmacaoOpened(false)}>
                Cancelar
              </Button>
              <Button primary small m={2} onClick={() => confirmarReemitirGuia()}>
                Continuar
              </Button>
            </Modal.Footer>
          </Modal>
          <div className={styles.titleContainer}>
            <span className={styles.title}>Status do Pagamento</span>
          </div>
          <div className={styles.buttonContainer}>{getButtonByStatusPagamento()}</div>
          <div className={styles.statusPagamentoContainer}>
            <StatusPagamentoCard
              status={getStatusByStepAndCardNumber(1)}
              description='Guia de recolhimento emitida'
              icon='guiaEmitida'
            />
            <div className={styles.separator + ' ' + styles[getStatusByStepAndCardNumber(1)]}></div>
            <StatusPagamentoCard
              status={getStatusByStepAndCardNumber(2)}
              description='Aguardando confirmação de pagamento'
              icon='aguardandoConfirmacao'
            />
            <div className={styles.separator + ' ' + styles[getStatusByStepAndCardNumber(2)]}></div>
            {stepStatusPagamento === 4 ? (
              <StatusPagamentoCard
                status={getStatusByStepAndCardNumber(3)}
                description='Pagamento rejeitado/cancelado/vencido'
                icon='pagamentoRejeitadoCanceladoVencido'
              />
            ) : (
              <StatusPagamentoCard
                status={getStatusByStepAndCardNumber(3)}
                description='Pagamento concluído'
                icon='pagamentoConcluido'
              />
            )}
          </div>
        </div>
      );
    }
  };

  return <div className={styles.statusPagamentoMainContainer}>{conteudo()}</div>;
}

export default StatusPagamento;
